const SNAPSHOT_FIELDS = [
    'taskTypeId', 'taskPriorityId', 'taskTitle', 'taskStatusId', 'sku', 'quantity', 'instructions',
    'amount', 'baseCurrencyId', 'conversionRate', 'baseConversionAmount', 'paymentModeId',
    'commissionBaseAmount', 'commissionRate', 'internalNotes', 'actualCost',
];

const normalize = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value).trim();
    const asNumber = Number(text);
    return text !== '' && !Number.isNaN(asNumber) ? String(asNumber) : text;
};

const toDayKey = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toDateString() : '';
};

// Captures the editable task fields as loaded from (or last saved to) the server.
export const toTaskSnapshot = (formData) => ({
    ...Object.fromEntries(SNAPSHOT_FIELDS.map((field) => [field, normalize(formData?.[field])])),
    entryDate: toDayKey(formData?.entryDate),
});

export const hasUnsavedTaskChanges = (formData, snapshot) => {
    if (!snapshot) return false;
    const current = toTaskSnapshot(formData);
    const hasFieldChange = Object.keys(snapshot).some((field) => snapshot[field] !== current[field]);
    const hasNewFiles = (formData?.attachments || []).some((file) => file?.isNew);
    const hasRemovedFiles = (formData?.attachmentActions || []).some((item) => item?.isDeleted);
    return hasFieldChange || hasNewFiles || hasRemovedFiles;
};

// Saves the task first so the charge uses what is on screen. Resolves true on success.
export const persistTaskBeforeCharge = async (updateTaskFn, taskData, { setLoader, onFailure }) => {
    setLoader(true);
    try {
        const response = await updateTaskFn(taskData);
        if (response?.data?.Status === 200) return true;
        if (!response?.data?._readOnlyBlocked) {
            onFailure(response?.data?.Message?.AppStatusDescription || response?.data?.Message || 'Failed to update task. Please try again.');
        }
    } catch (error) {
        if (!error?.response?.data?._readOnlyBlocked) {
            onFailure('An error occurred while updating the task.');
        }
    } finally {
        setLoader(false);
    }
    return false;
};

// Uploads files added on screen without leaving the page. Resolves true when there is nothing to upload or it succeeds.
export const uploadNewTaskAttachments = async (uploadFn, taskId, attachments, { setLoader, onFailure }) => {
    const newFiles = (attachments || []).filter((file) => file?.isNew);
    if (newFiles.length === 0) return true;

    const body = new FormData();
    newFiles.forEach((file) => body.append('files', file));
    body.append('TaskId', taskId);

    setLoader(true);
    try {
        const response = await uploadFn(body);
        if (response?.status === 200) return true;
        onFailure('Failed to save attachments. Please try again.');
    } catch (error) {
        onFailure('An error occurred while saving attachments.');
    } finally {
        setLoader(false);
    }
    return false;
};
